'use strict';
namespace Attractions {

    enum ShowBeforeOrAfter {
        Both, Before, After
    }

    interface ComboItem {
        Url: string|null;
        NewWindow: boolean;
        Title: string|null;
        Description: string|null;
        FastPass: boolean;
        FreeParking: boolean;
        MemoryMaker: boolean;
        DinnerCard: boolean;
        ParksIncluded: number[];
        Prices: ComboItemPrice[];
        ShowBeforeOrAfter: number;
    };

    interface ComboItemPrice {
        Currency: string;
        Key: string|null;
        Price: number;
        Type: string;
    }

    interface ComboFilter {
        id:number;
        img: string;
        title: string;
        included:never[]|ComboIncluded[];
    };

    interface ComboIncluded {
        img: string;
        title: string;
        available: ShowBeforeOrAfter;
    }

    class ThemeParkCombo {
        private comboItems:ComboItem[];
        private comboFilters:ComboFilter[];
        private checkedFilters: {[key:string]:boolean};
        private showMobileFilters:boolean = false;
        private pricesSorted:boolean = false;
        private afterDateVal:string = '2017-05-01T00:00:00.000Z';
        private afterDate:boolean = false; // Value of the toggle filter
        private isAfterDate:boolean = false; // True if today is actually after the afterDateVal
        private hasItems:boolean = true;

        constructor() {
            this.isAfterDate = this.checkIsAfterDate();
            this.comboFilters = [
                { id: 1
                , img: 'disney_logos/WDW-blk.jpg'
                , title: 'Walt Disney World Resort'
                , included: [
                    { img: 'disney_logos/Disneys_Magic_Kingdom.jpg'
                    , title: 'Magic Kingdom'
                    , available: ShowBeforeOrAfter.Both
                    },
                    { img: 'disney_logos/Disneys_Animal_Kingdom.jpg'
                    , title: 'Animal Kingdom'
                    , available: ShowBeforeOrAfter.Both
                    },
                    { img: 'disney_logos/Disneys_Epcot.jpg'
                    , title: 'Epcot'
                    , available: ShowBeforeOrAfter.Both
                    },
                    { img: 'disney_logos/Disneys_Blizzard_Beach.jpg'
                    , title: 'Blizzard Beach Water Park'
                    , available: ShowBeforeOrAfter.Both
                    },
                    { img: 'disney_logos/Disneys_Hollywood_Studios.jpg'
                    , title: 'Disney\'s Hollywood Studios'
                    , available: ShowBeforeOrAfter.Both
                    },
                    { img: 'disney_logos/Disneys_Typhoon_Lagoon.jpg'
                    , title: 'Typhoon Lagoon Water Park'
                    , available: ShowBeforeOrAfter.Both
                    }]
                },
                { id: 2
                , img: 'seaworld_logos/SeaWorld-All.jpg'
                , title: 'SeaWorld Parks'
                , included: [
                    { img: 'seaworld_logos/SeaWorld.jpg'
                    , title: 'SeaWorld'
                    , available: ShowBeforeOrAfter.Both
                    },
                    { img: 'seaworld_logos/Busch_Gardens.jpg'
                    , title: 'Busch Gardens'
                    , available: ShowBeforeOrAfter.Both
                    },
                    { img: 'seaworld_logos/Aquatica.jpg'
                    , title: 'Aquatica'
                    , available: ShowBeforeOrAfter.Both
                    }]
                },
                { id: 3
                , img: 'universal_logos/Universal_Studios.png'
                , title: 'Universal Orlando Resort&trade;'
                , included: [
                    { img: 'universal_logos/universal-studios-florida.png'
                    , title: 'Universal Studios Florida&trade;'
                    , available: ShowBeforeOrAfter.Both
                    },
                    { img: 'universal_logos/Universal_Island_of_Adventure.jpg'
                    , title: 'Universal\'s Islands Of Adventure&trade;'
                    , available: ShowBeforeOrAfter.Both
                    },
                    { img: 'universal_logos/Universal_Volcano_Bay.jpg'
                    , title: 'Universal\'s Volcano Bay&trade;'
                    , available: ShowBeforeOrAfter.After
                    }]
                },
                { id: 4
                , img: 'legoland/Legoland.jpg'
                , title: 'LEGOLAND Florida Resort'
                , included: []
                },
                { id: 5
                , img: 'kennedy-space-center/kennedy-space-center.jpg'
                , title: 'Kennedy Space Center'
                , included: []
                }
            ];
        }

        getItems():ComboItem[] {
            if (!this.pricesSorted) {
                this.pricesSorted = true;
                this.comboItems.map((item:ComboItem) =>
                    item.Prices = item.Prices.sort((a:ComboItemPrice, b:ComboItemPrice):number =>
                        a.Type.toLowerCase() === 'adult' ? -1 : 1));
            }

            const selectedParkIds:number[] = _.map(this.checkedFilters, (val:boolean, key:string) =>
                val ? parseInt(key, 10) : 0).filter((val:number) => val > 0);

            const beforeOrAfterItems = this.comboItems.filter((item:ComboItem) =>
                (item.ShowBeforeOrAfter === ShowBeforeOrAfter.Both)
                    || (item.ShowBeforeOrAfter === ShowBeforeOrAfter.Before && !this.afterDate)
                        || (item.ShowBeforeOrAfter === ShowBeforeOrAfter.After && this.afterDate));

            const filteredItems = !selectedParkIds.length ? beforeOrAfterItems :
                beforeOrAfterItems.filter((item:ComboItem) => {
                    const filterId:number = item.ParksIncluded
                        .reduce((res:number, val:number) => res|Math.pow(2, val-1), 0);
                    return selectedParkIds
                        .map((parkId:number) => Math.pow(2, parkId-1) & filterId)
                        .every((parkFilterId:number) => parkFilterId > 0);
                });

            this.hasItems = filteredItems.length > 0;
            return filteredItems;
        }

        getFilters(limit:number|undefined):ComboFilter[] {
            return (typeof limit === 'number' && limit > 0) ? this.comboFilters.slice(0, limit) : this.comboFilters;
        }

        getFilter(num:number):ComboFilter {
            return this.comboFilters[num - 1];
        }

        hasFiltersSelected():boolean {
            return _.map(this.checkedFilters, (val:boolean, key:string) => val).some((val:boolean) => val);
        }

        getSelectedFilters():ComboFilter[] {
            return this.comboFilters.filter((filter:ComboFilter) => this.checkedFilters[filter.id]);
        }

        hasIncludedParksToDisplay(included:ComboIncluded[]):boolean {
            return included.map((i:ComboIncluded) => this.showIncludedPark(i)).some(t => t);
        }

        showIncludedPark(item:ComboIncluded):boolean {
            return item.available === ShowBeforeOrAfter.Both
                || (item.available === ShowBeforeOrAfter.Before && !(this.afterDate || this.isAfterDate))
                    || (item.available === ShowBeforeOrAfter.After && (this.afterDate || this.isAfterDate));
        }

        includesPark(parkId:number, comboItem:ComboItem):boolean {
            return comboItem.ParksIncluded.indexOf(parkId) > -1;
        }

        includesParkClass(parkId:number, comboItem:ComboItem):string {
            return this.includesPark(parkId, comboItem) ? 'icon-check' : 'icon-close';
        }

        checkIsAfterDate():boolean {
            return (new Date()) > (new Date(this.afterDateVal));
        }
    }

    angular
        .module('TR')
        .directive('themeParkCombo', (): ng.IDirective => {
            return <ng.IDirective>{
                restrict: 'E',
                replace: true,
                transclude: true,
                scope: {},
                bindToController: {
                    comboItems: '='
                },
                controller: ThemeParkCombo,
                controllerAs: 'tpc',
                compile: (el: any, attrs: any, transclude: any) =>
                    $scope => transclude($scope, clone => el.append(clone))
            };
        });
}
